import axios from "axios";

import S from "../store";
import { AuthActionCreators } from "../store/actions/auth";
import { toast } from "react-toastify";

axios.defaults.baseURL = process.env.REACT_APP_API_BASE;

const requestErrorInterceptor = (err) => {
  console.error("Axios Interceptor Error:", err);

  if (!err?.response) {
    toast("An unknown error occured!", { type: "error" });
  } else if (
    err.response?.status === 401 &&
    !err.config.url.includes("/auth/")
  ) {
    const refreshToken = S.store.getState().auth.tokens.refreshToken;
    if (refreshToken) {
      return APIManager.refreshToken(refreshToken)
        .then((res) => {
          const newAccessToken = res?.data?.tokens?.accessToken;
          if (!newAccessToken) {
            throw new Error("No new access token received");
          }

          S.store.dispatch(
            AuthActionCreators.refreshAccessToken(newAccessToken)
          );

          axios.defaults.headers.Authorization = `Bearer ${newAccessToken}`;
          err.config.headers.Authorization = `Bearer ${newAccessToken}`;

          return axios.request(err.config);
        })
        .catch((refreshError) => {
          console.error("Token refresh failed:", refreshError);

          S.store.dispatch(AuthActionCreators.logout());
          window.location.href = `/auth/login?referrer=${encodeURIComponent(
            window.location.pathname + window.location.search
          )}`;
          return Promise.reject(refreshError);
        });
    } else {
      console.log("No refresh token available, logging out...");
      S.store.dispatch(AuthActionCreators.logout());
      window.location.href = `/auth/login?referrer=${encodeURIComponent(
        window.location.pathname + window.location.search
      )}`;
    }
  } else if (err.response?.status < 600 || err.response?.status >= 400) {
    if (err.response?.status === 403) {
      toast("You do not have permission to perform this action.", {
        type: "error",
      });
    } else if (err.response?.data?.errors?.length) {
      toast(err.response?.data?.errors[0], { type: "error" });
    } else {
      toast("An unknown error occured!");
    }
  }

  return Promise.reject(err);
};

axios.interceptors.request.use((config) => {
  const isAuthEndpoint = config.url && config.url.includes("/auth/");
  const isS3Url = config.url && config.url.includes("amazonaws.com");

  if (!isAuthEndpoint && !isS3Url) {
    const accessToken = S?.store?.getState().auth?.tokens?.accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  if (isS3Url) {
    delete config.headers.Authorization;
  }

  return config;
}, requestErrorInterceptor);

axios.interceptors.response.use(
  (response) => response,
  requestErrorInterceptor
);

const APIManager = {
  activate: (credentials) => {
    return axios
      .post("/auth/activate", credentials, {
        headers: {
          Authorization: "",
        },
      })
      .then((res) => res.data);
  },

  authorize: (clientID, redirectURI, state) => {
    return axios
      .post(
        `/oauth/authorize?response_type=code&client_id=${clientID}&redirect_uri=${redirectURI}&state=${state}`
      )
      .then((res) => {
        return res.data;
      });
  },

  getClient: (clientId) => {
    return axios.get(`/oauth/clients/${clientId}`).then((res) => res.data);
  },

  login: (credentials) => {
    return axios
      .post("/auth/login", credentials, {
        headers: {
          Authorization: "",
        },
      })
      .then((res) => {
        return res.data;
      });
  },

  register: (credentials) => {
    return axios
      .post("/auth/register", credentials, {
        headers: {
          Authorization: "",
        },
      })
      .then((res) => res.data);
  },

  resetPassword: (credentials) => {
    return axios
      .post("/auth/resetpassword", credentials, {
        headers: {
          Authorization: "",
        },
      })
      .then((res) => res.data);
  },

  changepassword: (credentials) => {
    return axios
      .put("/auth/changepassword", credentials, {
        headers: {
          Authorization: "",
        },
      })
      .then((res) => res.data);
  },

  switchAccount: (account) => {
    return axios
      .get(`/auth/switch/${account}`, {
        headers: {
          Authorization: "",
        },
      })
      .then((res) => {
        return res.data;
      });
  },

  getProfile: () => {
    return axios.get("/auth/profile").then((res) => res.data);
  },

  refreshToken: (refreshToken) => {
    return axios.post("/auth/token", { refreshToken }).then((res) => res.data);
  },

  createPlayer: (data) => {
    return axios.post("/players", data).then((res) => res.data);
  },

  deletePlayer: (playerId) => {
    return axios.delete(`/players/${playerId}`).then((res) => res.data);
  },

  getPlayer: (playerId) => {
    return axios.get(`/players/${playerId}`).then((res) => res.data);
  },

  getPlayers: () => {
    return axios.get("/players").then((res) => res.data);
  },

  updatePlayer: (data) => {
    return axios.put(`/players/${data.id}`, data).then((res) => res.data);
  },

  createSite: (data) => {
    return axios.post("/sites", data).then((res) => res.data);
  },

  getSites: () => {
    return axios.get("/sites").then((res) => {
      return res.data;
    });
  },

  getSite: (siteId) => {
    return axios.get(`/sites/${siteId}`).then((res) => res.data);
  },

  getPayments: () => {
    return axios.get("/users/payments").then((res) => res.data);
  },

  getPaymentInfo: () => {
    return axios.get("/users/paymentinfo").then((res) => res.data);
  },

  savePaymentInfo: (params) => {
    return axios.post("/users/paymentinfo", params).then((res) => res.data);
  },

  updateSite: (data) => {
    return axios.put(`/sites/${data.id}`, data).then((res) => res.data);
  },

  getSiteReport: (siteId, params) => {
    return axios
      .post(`/sites/${siteId}/report`, params, {
        timeout: 15000,
      })
      .then((res) => res.data);
  },

  getAppReport: (params, selectedUser) => {
    return axios
      .post("/apps/report", { ...params, user: selectedUser })
      .then((res) => res.data);
  },

  getBusinessReport: (siteId, params) => {
    const path =
      siteId === "all-sites" || siteId === ""
        ? `/business/all-sites`
        : `/business/${siteId}`;

    return axios
      .post(path, params, {
        timeout: 15000,
      })
      .then((res) => res.data);
  },
  createVideo: (data) => {
    return axios.post("/videos", data).then((res) => res.data);
  },

  getVideos: (page = 0, perPage = 20, q) => {
    const searchParams = new URLSearchParams();

    searchParams.append("page", page);
    searchParams.append("perPage", perPage);
    if (q) searchParams.append("q", q);

    return axios.get(`/videos?${searchParams}`).then((res) => res.data);
  },

  getVideo: (videoId) => {
    return axios.get(`/videos/${videoId}`).then((res) => res.data);
  },

  deleteVideo: (videoId) => {
    return axios.delete(`/videos/${videoId}`).then((res) => res.data);
  },

  updateVideo: (data) => {
    return axios.put(`/videos/${data.id}`, data).then((res) => res.data);
  },

  getVideoPlayback: (videoId) => {
    return axios.get(`/videos/${videoId}/playback`).then((res) => res.data);
  },

  getOverallVideoAnalytics: (startDate, endDate) => {
    return axios
      .get(`/analytics/players?start=${startDate}&end=${endDate}`)
      .then((res) => res.data);
  },

  getRealtimePlayerData: () => {
    return axios.get("/analytics/players/realtime").then((res) => res.data);
  },

  getRealtimeVideoData: (videoId) => {
    return axios
      .get(`/analytics/videos/${videoId}/realtime`)
      .then((res) => res.data);
  },

  getSingleVideoAnalytics: (videoId, startDate, endDate) => {
    return axios
      .get(`/analytics/videos/${videoId}?start=${startDate}&end=${endDate}`)
      .then((res) => res.data);
  },

  getScheduledReports: (reportType) => {
    return axios.post("/reports", { reportType }).then((res) => res.data);
  },

  getScheduledReport: (reportId) => {
    return axios.post("/reports/get", { reportId }).then((res) => res.data);
  },

  updateScheduledReport: (reportId, newData) => {
    return axios
      .put("/reports/update", { reportId, newData })
      .then((res) => res.data);
  },

  deleteScheduledReport: (reportId) => {
    return axios.post("/reports/delete", { reportId }).then((res) => res.data);
  },

  createScheduledReport: (reportData) => {
    return axios
      .post("/reports/create", { reportData })
      .then((res) => res.data);
  },

  exportToDrive: (fileName, csvString) => {
    return axios
      .post("/export/drive", { fileName, csvString })
      .then((res) => res.data);
  },

  reportsFallback: (params, selectedUser, email, currency, reportType) => {
    return axios
      .post("/reports/fallback", {
        params: { ...params, selectedUser },
        email,
        currency,
        reportType,
      })
      .then((res) => res.data);
  },

  getAsyncReports: () => {
    return axios.get("/reports/fallback/reports").then((res) => res.data);
  },

  deleteAsyncReport: () => {
    return axios
      .delete("/reports/fallback/delete/:reportId")
      .then((res) => res.data);
  },

  getFilters: () => {
    return axios.get("/filters").then((res) => res.data);
  },

  getFilter: (filterId) => {
    return axios.get(`/filters/${filterId}`).then((res) => res.data);
  },

  saveFilter: (filterData) => {
    return axios.post("/filters", filterData).then((res) => res.data);
  },

  updateFilter: (filterId, updateData) => {
    return axios
      .put(`/filters/${filterId}`, updateData)
      .then((res) => res.data);
  },

  deleteFilter: (filterId) => {
    return axios.delete(`/filters/${filterId}`).then((res) => res.data);
  },

  getAppFilters: () => {
    return axios.get("/app-filters").then((res) => res.data);
  },

  getAppFilter: (filterId) => {
    return axios.get(`/app-filters/${filterId}`).then((res) => res.data);
  },

  createAppFilter: (filterData) => {
    return axios.post("/app-filters", filterData).then((res) => res.data);
  },

  updateAppFilter: (filterId, updateData) => {
    return axios
      .put(`/app-filters/${filterId}`, updateData)
      .then((res) => res.data);
  },

  deleteAppFilter: (filterId) => {
    return axios.delete(`/app-filters/${filterId}`).then((res) => res.data);
  },

  izleseneInitiate: (data) => {
    return axios.post("/video-uploads/initiate", data).then((res) => res.data);
  },

  izleseneUploadViaUrl: (data) => {
    return axios.post("/video-uploads/url", data).then((res) => res.data);
  },

  izleseneGetPresignedPartUrl: (data) => {
    return axios
      .post("/video-uploads/presigned-part", data)
      .then((res) => res.data);
  },

  izleseneComplete: (data) => {
    return axios.post("/video-uploads/complete", data).then((res) => res.data);
  },

  izleseneAbort: (data) => {
    return axios.post("/video-uploads/abort", data).then((res) => res.data);
  },

  izleseneUpdateVideo: (video) => {
    return axios
      .put(`/video-uploads/${video.id}`, {
        title: video.title,
        description: video.description,
      })
      .then((res) => {
        return res.data;
      });
  },

  izleseneDeleteVideo: (videoId) => {
    return axios.delete(`/video-uploads/${videoId}`).then((res) => res.data);
  },

  getAIVideos: ({ siteVirgulId, page = 0, perPage = 20, isPublished }) => {
    const searchParams = new URLSearchParams();
    searchParams.append("siteVirgulId", siteVirgulId);
    searchParams.append("page", page);
    searchParams.append("perPage", perPage);
    if (isPublished !== undefined) {
      searchParams.append("isPublished", isPublished);
    }
    return axios.get(`/ai-videos?${searchParams}`).then((res) => res.data);
  },

  getFetchedSiteNews: ({
    siteVirgulId,
    orderBy = "text",
    orderByDirection = "desc",
  }) => {
    return axios
      .get(
        `/ai-videos/fetched-site-news?siteVirgulId=${siteVirgulId}&orderBy=${orderBy}&orderByDirection=${orderByDirection}`
      )
      .then((res) => res.data);
  },

  fetchSiteNews: ({
    siteVirgulId,
    orderBy = "text",
    orderByDirection = "desc",
  }) => {
    return axios
      .post(`/ai-videos/fetch-site-news?siteVirgulId=${siteVirgulId}&orderBy=${orderBy}&orderByDirection=${orderByDirection}`)
      .then((res) => res.data);
  },

  saveAIVideo: async ({ siteVirgulId, overrideVideoId, ...fields }) => {
    const url = overrideVideoId
      ? `/ai-videos?siteVirgulId=${siteVirgulId}&overrideVideoId=${encodeURIComponent(overrideVideoId)}`
      : `/ai-videos?siteVirgulId=${siteVirgulId}`;
    return axios.post(url, fields).then((res) => res.data);
  },

  getAIVideo: ({ siteVirgulId, videoId }) => {
    return axios
      .get(`/ai-videos/${videoId}?siteVirgulId=${siteVirgulId}`)
      .then((res) => res.data);
  },

  getAIVideoTemplates: ({ siteVirgulId }) => {
    return axios
      .get(`/ai-videos/templates?siteVirgulId=${siteVirgulId}`)
      .then((res) => res.data);
  },

  saveAIVideoCreationSettings: ({
    siteVirgulId,
    templateName,
    templateSettings,
    contentSettings,
    regenerationSettings,
    isActive,
  }) => {
    return axios
      .post(`/ai-videos/creation-settings?siteVirgulId=${siteVirgulId}`, {
        templateName,
        templateSettings,
        contentSettings,
        regenerationSettings,
        isActive,
      })
      .then((res) => res.data);
  },

  getAIVideoCreationSettings: ({ siteVirgulId }) => {
    return axios
      .get(`/ai-videos/creation-settings?siteVirgulId=${siteVirgulId}`)
      .then((res) => res.data);
  },

  publishAIVideo: ({ siteVirgulId, videoId }) => {
    return axios
      .post(`/ai-videos/${videoId}/publish?siteVirgulId=${siteVirgulId}`)
      .then((res) => res.data);
  },

  unpublishAIVideo: ({ siteVirgulId, videoId }) => {
    return axios
      .post(`/ai-videos/${videoId}/unpublish?siteVirgulId=${siteVirgulId}`)
      .then((res) => res.data);
  },

  deleteAIVideo: ({ siteVirgulId, videoId }) => {
    return axios
      .delete(`/ai-videos/${videoId}?siteVirgulId=${siteVirgulId}`)
      .then((res) => res.data);
  },

  // izleseneGetProgress: (uploadId) => {
  //   return axios
  //     .get(`/video-uploads/progress/${uploadId}`)
  //     .then((res) => res.data);
  // },
};

export default APIManager;
